import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Check, CreditCard, Sparkles, ArrowUpCircle, ExternalLink, Minus } from 'lucide-react'
import type { Feature } from '@shared/subscription'
import {
  useSubscriptionPlans,
  useTenantSubscription,
  useUpgradeSubscription,
  useCreateCheckout,
  useCreatePortalSession,
  useCancelSubscription,
  useStaffUserCount,
} from '@/hooks/data/useSubscriptions'
import { useOrderAnalyticsSummary } from '@/hooks/data/useOrderAnalytics'
import type { SubscriptionBillingCycle } from '@/types/domain'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'

const UNLIMITED = 2147483647

function UsageBar({ label, used, limit }: { label: string; used: number; limit: number }) {
  const unlimited = limit >= UNLIMITED
  const pct = unlimited ? 0 : Math.min(100, (used / Math.max(limit, 1)) * 100)
  const nearLimit = !unlimited && pct >= 80
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className={nearLimit ? 'font-medium text-amber-600' : 'text-gray-500 dark:text-gray-400'}>
          {used.toLocaleString()} {unlimited ? '' : `/ ${limit.toLocaleString()}`}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className={`h-full rounded-full ${nearLimit ? 'bg-amber-500' : 'bg-blue-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  )
}

const featureLabel: Record<Feature, string> = {
  'booking.online': 'Online booking',
  'scheduling.optimizer': 'Schedule optimizer',
  'workers.geofence': 'Worker geofencing',
  'billing.invoicing': 'Invoicing',
  'billing.accounting_export': 'Accounting export',
  'analytics.dashboards': 'Analytics dashboards',
  'campaigns.promos': 'Campaigns & promos',
  'ai.chat': 'AI chat assistant',
  'ai.auto_reschedule': 'AI auto-reschedule',
  'ai.dunning': 'AI dunning',
  'cms.multi_brand': 'Multi-brand CMS',
  'tenant.sso': 'SSO / SAML',
  'tenant.audit_log': 'Audit log',
  'marketplace.contractors': 'Contractor marketplace',
}

/**
 * Own-tenant self-service billing — current plan, usage vs. limits, plan catalog to
 * subscribe/switch. The cross-tenant plan-manager/all-stores/analytics sections that used to
 * live here moved to /platform/stores (platform admin only) once Phase 1a locked those
 * endpoints down to a real platform_admin claim — a store's own "admin" role can no longer
 * reach them, so they couldn't stay on a page that role can navigate to. See
 * abundant-stirring-bee.md's Phase 2 section.
 */
function BillingPage() {
  const { data: activePlans = [] } = useSubscriptionPlans(false)
  const { data: subscription, isLoading: subLoading } = useTenantSubscription()
  const { data: staffCount } = useStaffUserCount()
  const { data: orderSummary } = useOrderAnalyticsSummary(30)

  const upgrade = useUpgradeSubscription()
  const createCheckout = useCreateCheckout()
  const createPortal = useCreatePortalSession()
  const cancel = useCancelSubscription()
  const confirmDelete = useDeleteConfirm()

  const [billingCycle, setBillingCycle] = useState<SubscriptionBillingCycle>('Monthly')

  const allFeatures = Array.from(new Set(activePlans.flatMap((p) => p.features))) as Feature[]

  // Real usage vs. the current plan's limits — staffCount/orderSummary are live counts, not
  // fabricated. Storage isn't tracked anywhere in this system (media rows store URLs, not
  // sizes), so there's no real number to show for it — deliberately omitted rather than faked.
  const usage = useMemo(() => {
    const plan = subscription?.plan
    if (!plan || subscription?.status !== 'Active') return null
    const usersUsed = staffCount ?? 0
    const ordersUsed = orderSummary?.totalOrders ?? 0
    const usersPct = plan.maxUsers >= UNLIMITED ? 0 : usersUsed / Math.max(plan.maxUsers, 1)
    const ordersPct = plan.maxOrders >= UNLIMITED ? 0 : ordersUsed / Math.max(plan.maxOrders, 1)
    const nearLimit = usersPct >= 0.8 || ordersPct >= 0.8

    const nextTier = activePlans
      .filter((p) => p.sortOrder > plan.sortOrder)
      .sort((a, b) => a.sortOrder - b.sortOrder)[0]

    let nudge: string | null = null
    if (nearLimit && nextTier) {
      if (usersPct >= ordersPct && plan.maxUsers < UNLIMITED) {
        const remaining = plan.maxUsers - usersUsed
        const nextLabel = nextTier.maxUsers >= UNLIMITED ? 'unlimited' : nextTier.maxUsers
        nudge = remaining <= 0
          ? `You've exceeded your plan's user limit by ${-remaining || 1} — ${nextTier.name} supports ${nextLabel} users.`
          : `${remaining} user${remaining === 1 ? '' : 's'} away from your plan's limit — ${nextTier.name} supports ${nextLabel} users.`
      } else if (plan.maxOrders < UNLIMITED) {
        const remaining = plan.maxOrders - ordersUsed
        const nextLabel = nextTier.maxOrders >= UNLIMITED ? 'unlimited' : nextTier.maxOrders.toLocaleString()
        nudge = remaining <= 0
          ? `You've exceeded your plan's monthly order limit — ${nextTier.name} supports ${nextLabel} orders/mo.`
          : `${remaining} order${remaining === 1 ? '' : 's'} away from your plan's monthly limit — ${nextTier.name} supports ${nextLabel} orders/mo.`
      }
    }

    return { usersUsed, ordersUsed, maxUsers: plan.maxUsers, maxOrders: plan.maxOrders, nudge, nextTier }
  }, [subscription, staffCount, orderSummary, activePlans])

  const doUpgrade = async (planId: string) => {
    try {
      await upgrade.mutateAsync({ planId, billingCycle })
      toast.success('Subscription updated.')
    } catch (err) {
      toast.error(userMessage(err))
    }
  }

  /** Real Stripe Checkout — redirects to Stripe's own hosted page, distinct from doUpgrade's
   * instant admin-assigned switch above (no charge). Both stay available: doUpgrade for
   * admin-assigned plans (e.g. comped accounts), this for a tenant actually paying by card. */
  const doCheckout = async (planId: string) => {
    try {
      const { url } = await createCheckout.mutateAsync({ planId, billingCycle })
      window.location.href = url
    } catch (err) {
      toast.error(userMessage(err))
    }
  }

  return (
    <div className="p-2 md:p-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <CreditCard className="w-6 h-6" /> Billing &amp; Subscription
        </h1>
        <p className="text-gray-600 dark:text-gray-400">Real plan catalog and subscription data (SubscriptionsController).</p>
      </div>

      {/* Current subscription */}
      <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="text-sm text-gray-500">Current plan</span>
          <div className="text-xl font-bold text-gray-800 dark:text-white">
            {subLoading ? 'Loading…' : subscription?.plan?.name ?? 'No plan assigned'}
          </div>
          {subscription && (
            <div className="text-xs text-gray-400 mt-0.5">{subscription.billingCycle} billing · started {new Date(subscription.startedAt).toLocaleDateString()}</div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {subscription && (
            <div className="text-right text-sm">
              <span
                className={`px-2 py-0.5 rounded-full capitalize ${
                  subscription.status === 'Active' ? 'bg-green-100 text-green-700' : subscription.status === 'Cancelled' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'
                }`}
              >
                {subscription.status}
              </span>
              {subscription.renewsAt && subscription.status === 'Active' && (
                <div className="text-gray-400 mt-1">renews {new Date(subscription.renewsAt).toLocaleDateString()}</div>
              )}
              {subscription.cancelledAt && <div className="text-gray-400 mt-1">cancelled {new Date(subscription.cancelledAt).toLocaleDateString()}</div>}
            </div>
          )}
          {subscription?.stripeCustomerId && (
            <button
              onClick={async () => {
                try {
                  const { url } = await createPortal.mutateAsync()
                  window.location.href = url
                } catch (err) {
                  toast.error(userMessage(err))
                }
              }}
              disabled={createPortal.isPending}
              className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Manage billing
            </button>
          )}
          {subscription?.status === 'Active' && (
            <button
              onClick={() =>
                confirmDelete('your subscription', async () => {
                  await cancel.mutateAsync()
                  toast.success('Subscription cancelled — access continues until it renews.')
                })
              }
              className="px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
            >
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      {/* Usage vs. plan limits */}
      {usage && (
        <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-white">Usage</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <UsageBar label="Staff users" used={usage.usersUsed} limit={usage.maxUsers} />
            <UsageBar label="Orders (last 30 days)" used={usage.ordersUsed} limit={usage.maxOrders} />
          </div>
          {usage.nudge && (
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <ArrowUpCircle className="w-4 h-4 shrink-0" />
              {usage.nudge}
              {usage.nextTier && (
                <button
                  onClick={() => doUpgrade(usage.nextTier!.id)}
                  className="ml-auto shrink-0 font-medium underline hover:no-underline"
                >
                  Upgrade to {usage.nextTier.name}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Billing cycle toggle */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Billing cycle:</span>
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm">
          {(['Monthly', 'Annual'] as SubscriptionBillingCycle[]).map((c) => (
            <button
              key={c}
              onClick={() => setBillingCycle(c)}
              className={`px-3 py-1.5 ${billingCycle === c ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {activePlans.map((p) => {
          const current = p.id === subscription?.planId && subscription?.status === 'Active'
          const price = billingCycle === 'Annual' ? p.annualPrice : p.monthlyPrice
          return (
            <div key={p.id} className={`rounded-xl border p-5 flex flex-col ${current ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 dark:border-gray-700'} bg-white dark:bg-gray-800`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-1">
                  {p.name}
                  {p.name === 'Enterprise' && <Sparkles className="w-4 h-4 text-amber-500" />}
                </h3>
                {current && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Current</span>}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{p.description}</p>
              <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                ${price.toFixed(0)}<span className="text-sm font-normal text-gray-400">/{billingCycle === 'Annual' ? 'yr' : 'mo'}</span>
              </div>
              <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                <div>{p.maxUsers >= 2147483647 ? 'Unlimited' : p.maxUsers} users</div>
                <div>{p.maxOrders >= 2147483647 ? 'Unlimited' : p.maxOrders.toLocaleString()} orders/mo</div>
                <div>{p.storageGb} GB storage</div>
              </div>
              <ul className="mt-4 space-y-1.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Check className="w-4 h-4 text-green-500 shrink-0" /> {featureLabel[f as Feature] ?? f}
                  </li>
                ))}
              </ul>
              {!current && (
                <button
                  disabled={createCheckout.isPending}
                  onClick={() => doCheckout(p.id)}
                  className="mt-4 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white disabled:opacity-40"
                >
                  <CreditCard className="w-4 h-4" /> Pay with card
                </button>
              )}
              <button
                disabled={current || upgrade.isPending}
                onClick={() => doUpgrade(p.id)}
                title={current ? undefined : 'Admin-assigned — no real charge'}
                className={`mt-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  current ? 'bg-gray-100 text-gray-400 dark:bg-gray-700' : 'border border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                }`}
              >
                {current ? 'Current plan' : subscription ? 'Switch (no charge)' : 'Subscribe (no charge)'}
              </button>
            </div>
          )
        })}
      </div>

      {/* Feature matrix */}
      <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 font-semibold text-gray-800 dark:text-white">Feature comparison</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Feature</th>
                {activePlans.map((p) => <th key={p.id} className="px-4 py-2">{p.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {allFeatures.map((f) => (
                <tr key={f} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-200">{featureLabel[f] ?? f}</td>
                  {activePlans.map((p) => (
                    <td key={p.id} className="px-4 py-2 text-center">
                      {p.features.includes(f) ? <Check className="w-4 h-4 text-green-500 inline" /> : <Minus className="w-4 h-4 text-gray-300 inline" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/billing/')({
  component: BillingPage,
})
